import { useEffect, useState } from "react";
import { ProcedureCardsGrid, DocumentCardsList } from "./documentcards";
import DocumentHeader from "./documentHeader";
import TabsComponent from "../tabswitches";
import dayjs from "dayjs";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import ProcedureDocument from "./ProcedureDocument";
import { CircularProgress } from "@mui/material";
import api from "../../../../api/api";
import careiqapi from "../../../../api/careiqapi";
import DropdownPill from "./DropdownPill";
import { useSelector } from "react-redux";
import { DocumentAllCards } from "./documentAllCards";
import PaginationDropdownPill from "./PaginationDropdownPill";
import openEmrinstance from "../../../../api/emrapiInstance";
import Base64ViewerModal from "./base64modal";
import ViewDoc from "../forms/htmls/homesleep";
import ViewPhq9 from "../forms/htmls/phq9";
import ViewMdq from "../forms/htmls/mdq";
import ViewAsrs from "../forms/htmls/asrsform";
import ViewMmse from "../forms/htmls/mmse";
import ViewStopBang from "../forms/htmls/stopbang";
import OnBoardView from "../../../../onboarding/OnBoardView";
import { dropImagingReportTwins } from "./documentTwins";
import { getPracticeId } from "../../../../config/practice";

const formatDate = (dateString) => dayjs(dateString).format("YYYY-MM-DD");

// Athena's FHIR feed lists each visit note as separate HPI/ROS/PE/A&P
// SECTION resources (a-9928.apNote-460092, a-9928.rosNote-460092, ...),
// every one titled "History and physical note" — so the Documents tab
// showed 3-4 identical-looking rows per visit ("a lot of documents are
// duplicated", QA 2026-09-03). Collapse to one row per encounter; prefer
// the A&P section so View opens the most useful part of the note.
const SECTION_ID_RE = /^a-\d+\.(hpi|ros|pe|ap)Note-(\d+)$/;
const SECTION_RANK = { ap: 0, hpi: 1, pe: 2, ros: 3 };
const collapseEncounterSections = (docs) => {
  const byEncounter = new Map();
  const passthrough = [];
  (docs || []).forEach((d) => {
    const m = SECTION_ID_RE.exec(String(d?.encounterdocumentid || ""));
    if (!m) {
      passthrough.push(d);
      return;
    }
    const rank = SECTION_RANK[m[1]] ?? 9;
    const current = byEncounter.get(m[2]);
    if (!current || rank < current.rank) byEncounter.set(m[2], { doc: d, rank });
  });
  return [...passthrough, ...[...byEncounter.values()].map((v) => v.doc)];
};

const Document = ({ searchdate, dataId }) => {
  // Signed-in user (/auth/me via redux). Declared first: the upload handler
  // below reads it. It used to read localStorage "userConfig", which nothing
  // in the app writes — so every Documents-tab upload went out as dept 1 with
  // no uploader, and the review-task POST carried provider "" which v1
  // orders/create rejects with 400 (QA 2026-09-30: no review task for the
  // Dallas EKG uploads, which were also stamped department 1).
  const userConfig = useSelector((state) => state.userconfig);
  // ── Local document upload (stored in the documents cache, visible on the
  // timeline immediately; user can sync to Athena later from the viewer) ──
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadType, setUploadType] = useState("clinicaldocument");
  const [uploadDesc, setUploadDesc] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadMsg, setUploadMsg] = useState("");
  const practiceIdForUpload = getPracticeId();
  const handleLocalUpload = async () => {
    if (!uploadFile) { setUploadMsg("Choose a file first"); return; }
    setUploadBusy(true);
    setUploadMsg("");
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("doctype", uploadType);
      fd.append("description", uploadDesc || uploadFile.name);
      const deptForUpload = Number(userConfig?.config?.departmentid) || 1;
      fd.append("departmentid", String(deptForUpload));
      fd.append("uploaded_by", userConfig?.username || "");
      let upRes;
      try {
        upRes = await careiqapi.post(
          `/v1/fhir-mirror/${practiceIdForUpload}/patient/${dataId?.patientid}/documents/upload`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
      } catch (dupErr) {
        // The same file is already in this chart (2026-10-06): nothing stored, and no
        // second review task - the first upload has one.
        if (dupErr?.response?.status === 409 && dupErr?.response?.data?.code === "duplicate_upload") {
          setUploadMsg(dupErr.response.data.message);
          setUploadDesc(""); setUploadFile(null);
          return;
        }
        throw dupErr;
      }
      if (upRes?.duplicate_prevented) {
        // the gateway answered with the copy already in this chart: nothing new stored
        setUploadMsg(upRes.message || "This file is already in this chart.");
        setUploadDesc(""); setUploadFile(null);
        return;
      }
      // Provider review task — same pattern as the fax send-to-chart flow
      // (sendtoemr.jsx). The Documents-tab upload never created one, so
      // uploads stopped "showing up in the task" once staff switched to
      // this button (QA 2026-09-04).
      try {
        const docId = upRes?.documentid || upRes?.data?.documentid;
        const providerName =
          `${userConfig?.firstname || ""} ${userConfig?.lastname || ""}`.trim() ||
          userConfig?.username || "";
        const typeLabel = uploadType === "imagingresult" ? "imaging"
          : uploadType === "encounterdocument" ? "encounter document" : "clinical document";
        await api.post(`/v1/${practiceIdForUpload}/patient/${dataId?.patientid}/orders/create`, {
          department_id: deptForUpload,
          patient_name: `${dataId?.firstname || ""} ${dataId?.lastname || ""}`.trim() || dataId?.name || "",
          tov: "FOLLOW UP",
          lov: 0,
          diagnosis: "",
          ordertype: "Task",
          ordername: `Review ${typeLabel} (uploaded) — from chart timeline`,
          schedule: "f",
          provider: providerName,
          user_role: "ALL",
          status: "CREATED",
          confirm_status: "C",
          orderdate: dayjs().format("YYYY-MM-DD"),
          encounterid: 0,
          ...(docId
            ? { result: `docref:${docId}:${dataId?.patientid}:${uploadType}` }
            : {}),
        });
        setUploadMsg("Uploaded — it shows on the timeline and a review task was created.");
      } catch (taskErr) {
        console.error("review-task create failed", taskErr);
        setUploadMsg("Uploaded — it now shows on the patient timeline (review task could not be created).");
      }
      setUploadDesc(""); setUploadFile(null);
    } catch (e) {
      setUploadMsg(e?.response?.data?.message || "Upload failed");
    } finally {
      setUploadBusy(false);
    }
  };
  const [view, setView] = useState(1);
  const [selectedTab, setSelectedTab] = useState(0);
  const [procedureData, setProcedureData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // AI/KG document labels — the SAME map the CareIQ timeline overlays, so
  // both views show identical names ("Sleep Study Report", not the raw
  // "Clinical Document" description). Cache-side fetch, keyed per patient.
  const [docLabels, setDocLabels] = useState({});
  useEffect(() => {
    // patientId/practiseid consts are declared BELOW this effect — reference
    // the sources directly or the whole tab TDZ-crashes to a blank page.
    const pid = dataId?.patientid;
    if (!pid) return;
    let cancelled = false;
    api.get(`/v1/kg/${getPracticeId()}/todo/${pid}`)
      .then((res) => {
        if (cancelled) return;
        const d = res?.data || res;
        const kgLabels = d?.document_labels || d?.patient_insights?.document_labels || [];
        const map = {};
        (Array.isArray(kgLabels) ? kgLabels : []).forEach((dl) => {
          if (dl.documentid && dl.label) map[dl.documentid] = dl.label;
        });
        setDocLabels(map);
      })
      .catch(() => { /* labels are an enhancement — raw names still render */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataId?.patientid]);
  const [isProcedureDocumentOpen, setIsProcedureDocumentOpen] = useState(false);
  const [searchDate, setSearchDate] = useState(formatDate(searchdate));
  const [selectedProcedure, setSelectedProcedure] = useState({});
  const [encounterData, setEncounterData] = useState([]);
  const [subview, setSubView] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedData, setSelectedData] = useState({});
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [selectedview, setSelectedView] = useState({});
  const [ismodal, setIsModal] = useState(false);
  const [openBase64Viewer, setOpenBase64Viewer] = useState(false);
  const [htmlModal, setHtmlModal] = useState(false);
  const patientId = dataId.patientid;
  const practiseid = getPracticeId();
  const apikey = process.env.REACT_APP_OPENEMR_KEY;
  const CLINICAL_CATEGORY_IDS = [32, 33, 2, 13];
  // v2 mirror document list: Athena docs + local uploads (faxes sent to
  // chart, migrated EMR docs) in ONE call — the v1 clinical-documents list
  // is Athena-only, so faxed docs never appeared here (QA 2026-08-24).
  const fetchMirrorList = async (cat) => {
    const wrapKey = `${cat}s`;
    const mres = await careiqapi.get(
      `/v1/fhir-mirror/${practiseid}/patients/${patientId}/documents/${cat}`,
    );
    return mres?.data?.[wrapKey] || mres?.[wrapKey] || [];
  };
  const categoryId = view === 2 ? 33 : view === 1 ? 32 : view === 4 ? 2 : 13;
  // Remove a just-deleted local upload from the rendered list without a
  // full refetch (the backend already removed it from store + cache).
  const handleLocalDeleted = (doc) => {
    const key =
      doc?.clinicaldocumentid ?? doc?.imagingresultid ?? doc?.encounterdocumentid;
    setProcedureData((prev) =>
      prev.filter(
        (d) =>
          (d?.clinicaldocumentid ?? d?.imagingresultid ?? d?.encounterdocumentid) !==
          key,
      ),
    );
  };
  const fetchOpenEmrDocs = async () => {
    // Retired 2026-09-03: this queried the frisco OpenEMR box with the CARE
    // patientid, but OpenEMR keys documents by its OWN patient ids — the call
    // never matched the right chart (empty for real patients, and a wrong-
    // chart risk if the id spaces ever collide). The migrated OpenEMR docs
    // now come from the v2 mirror's local store (migrate_emr_docs.py 08-20),
    // which the mirror lists above already include.
    return [];
  };
  const fetchFormsData = async () => {
    setIsLoading(true);
    try {
      const res = await api.get(
        `/v1/${practiseid}/pcard/${patientId}/formHistory`,
      );

      const forms = Array.isArray(res.data)
        ? res.data.map((item) => ({
            id: item.id,
            title: item.form_name,
            form_name: item.form_name,
            form_id: item.form_id,
            recordid: item.id,

            createddate: dayjs(item.created_at),
            normalizedDate: dayjs(item.created_at),

            documentclass: "Form",
            source: "form",

            html_url: item.html_url,
            pdf_url: item.pdf_url,
          }))
        : [];

      // 🔥 always latest first
      setProcedureData(
        forms.sort(
          (a, b) =>
            dayjs(b.createddate).valueOf() - dayjs(a.createddate).valueOf(),
        ),
      );
    } catch (err) {
      console.error("Error fetching forms data", err);
      setProcedureData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        const formsRes = await api.get(
          `/v1/${practiseid}/pcard/${patientId}/formHistory`,
        );
        const formsDocs = Array.isArray(formsRes.data)
          ? formsRes.data.map((item) => ({
              id: item.id,
              title: item.form_name,
              form_name: item.form_name,
              form_id: item.form_id,
              recordid: item.id,

              // 🔑 REQUIRED FIELDS
              createddate: dayjs(item.created_at),
              normalizedDate: dayjs(item.created_at),

              // 👇 identify as form
              documentclass: "Form",
              source: "form",

              // 👇 needed for view
              html_url: item.html_url,
              pdf_url: item.pdf_url,
            }))
          : [];
        const [
          procedureRes,
          encounterRes,
          clinicalList,
          emrDocsRes,
          onboardingResponse,
          mirrorImaging,
          mirrorEncounter,
        ] = await Promise.all([
          api.post(`/v1/${practiseid}/patient/${patientId}/procedures/get`, {
            category: "Procedures",
            department_id: userConfig.config.departmentid,
          }),
          api.post(`/v1/${practiseid}/patient/${patientId}/procedures/get`, {
            category: "ProgressNotes",
            department_id: userConfig.config.departmentid,
          }),
          fetchMirrorList("clinicaldocument").catch(() => []),
          // OpenEMR live fetch retired 2026-09-03 (wrong id space — see
          // fetchOpenEmrDocs); migrated docs come from the mirror local store.
          Promise.resolve([]),
          api.get(`/v1/${practiseid}/onboarding/get_all_patients`),
          // local-only extras for the All view (faxes/migrated docs) — the
          // Athena procedures/get lists above can't see them
          fetchMirrorList("imagingresult").catch(() => []),
          fetchMirrorList("encounterdocument").catch(() => []),
        ]);
        // ----------- ONBOARDING LOGIC -----------
        const onboardingData =
          onboardingResponse?.status === 200
            ? onboardingResponse.data || []
            : [];

        const filteredOnboarding = onboardingData.filter(
          (item) => item?.patientid == patientId,
        );
        console.log(filteredOnboarding, "onboard");
        const normalizedOnboarding = filteredOnboarding.map((item) => ({
          ...item,

          exam: item?.fullname || "Patient Onboarding",
          document_data: "Onboarding Form",

          docdate: dayjs(item.created_at, "YYYY-MM-DD"),
          normalizedDate: dayjs(item.created_at, "YYYY-MM-DD HH:mm:ss"),
          createddate: dayjs(item.created_at, "YYYY-MM-DD HH:mm:ss"),

          documentclass: "Onboarding",
          source: "onboarding",
        }));
        const imagingData =
          procedureRes.data.imagingresults?.map((item) => ({
            ...item,
            normalizedDate: dayjs(item.createddate),
            createddate: dayjs(item.createddate),
            source: "imaging",
          })) || [];

        const encounterDocs = collapseEncounterSections(
          encounterRes.data.encounterdocuments || [],
        ).map((item) => ({
          ...item,
          normalizedDate: dayjs(item.createddate),
          createddate: dayjs(item.createddate),
          source: "encounter",
        }));

        // Each imaging result also arrives in the clinical list as its FHIR
        // "Cardiology Report" / "Radiology Report" twin — drop the twin when
        // the imaging row itself is on screen (see documentTwins.js; the
        // mirror imaging list pairs procedures/get's FHIR ids with the
        // numeric id the twin carries).
        const clinicalDocs = dropImagingReportTwins(clinicalList, imagingData, mirrorImaging).map((item) => ({
          ...item,
          normalizedDate: dayjs(item.createddate),
          createddate: dayjs(item.createddate),
          source: "clinical",
        }));

        // Faxes / migrated docs uploaded to the chart (mirror local store) —
        // append only local rows; Athena rows already came from procedures/get.
        const localExtras = [
          ...mirrorImaging.filter((d) => d?.local).map((item) => ({
            ...item,
            normalizedDate: dayjs(item.createddate),
            createddate: dayjs(item.createddate),
            source: "imaging",
          })),
          ...mirrorEncounter.filter((d) => d?.local).map((item) => ({
            ...item,
            normalizedDate: dayjs(item.createddate),
            createddate: dayjs(item.createddate),
            source: "encounter",
          })),
        ];

        const emrDocs = emrDocsRes.flatMap((res) =>
          (res.data || []).map((doc) => ({
            ...doc,
            normalizedDate: dayjs(doc.docdate, "YYYY-MM-DD"),
            createddate: dayjs(doc.docdate, "YYYY-MM-DD"),
            documentclass: "Emr",
            source: "openemr",
          })),
        );

        const merged = [
          ...normalizedOnboarding,
          ...imagingData,
          ...encounterDocs,
          ...clinicalDocs,
          ...localExtras,
          ...emrDocs,
          ...formsDocs,
        ];
        console.log(emrDocs, "merged");
        setProcedureData(merged);
      } catch (error) {
        console.error("Error fetching all data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // const fetchClinicalDocuments = async () => {
    //   setIsLoading(true);
    //   try {
    //     const response = await api.post(`v1/${practiseid}/clinical-documents`, {
    //       patient_id: patientId,
    //       department_id: userConfig.config.departmentid,
    //     });
    //     if (response.status === 200) {
    //       setProcedureData(response.data.clinicaldocuments || []);
    //     }
    //   } catch (error) {
    //     console.error("Error fetching clinical documents:", error);
    //   } finally {
    //     setIsLoading(false);
    //   }
    // };
    const fetchClinicalDocuments = async () => {
      setIsLoading(true);
      try {
        const [clinicalList, emrDocs] = await Promise.all([
          fetchMirrorList("clinicaldocument").catch(() => []),
          fetchOpenEmrDocs([13]), // 👈 Clinical
        ]);

        const clinicalDocs = clinicalList.map((item) => ({
          ...item,
          normalizedDate: dayjs(item.createddate),
          createddate: dayjs(item.createddate),
          source: "clinical",
        }));

        setProcedureData([...clinicalDocs, ...emrDocs]);
      } catch (error) {
        console.error("Error fetching clinical documents:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // const fetchProcedureData = async () => {
    //   setIsLoading(true);
    //   try {
    //     const payload = {
    //       category: subview === 1 ? "Procedures" : "ProgressNotes",
    //       department_id: userConfig.config.departmentid,
    //     };
    //     const response = await api.post(
    //       `/v1/${practiseid}/patient/${patientId}/procedures/get`,
    //       payload
    //     );

    //     if (response.status === 200) {
    //       subview === 1
    //         ? setProcedureData(response.data.imagingresults || [])
    //         : setProcedureData(response.data.encounterdocuments || []);
    //     }
    //   } catch (error) {
    //     console.error("Error fetching procedure data:", error);
    //   } finally {
    //     setIsLoading(false);
    //   }
    // };
    const fetchProcedureData = async () => {
      setIsLoading(true);
      try {
        const payload = {
          category: subview === 1 ? "Procedures" : "ProgressNotes",
          department_id: userConfig.config.departmentid,
        };

        const response = await api.post(
          `/v1/${practiseid}/patient/${patientId}/procedures/get`,
          payload,
        );

        const apiDocs =
          subview === 1
            ? response.data.imagingresults || []
            : collapseEncounterSections(response.data.encounterdocuments || []);

        const normalizedApiDocs = apiDocs.map((item) => ({
          ...item,
          normalizedDate: dayjs(item.createddate),
          createddate: dayjs(item.createddate),
          source: subview === 1 ? "imaging" : "encounter",
        }));
        console.log(subview, "subview");
        // 👇 OpenEMR integration
        const emrDocs = await fetchOpenEmrDocs([subview == 1 ? 32 : 33]);

        // Care local uploads (faxes sent to chart, migrated OpenEMR docs) live
        // in the v2 mirror, which procedures/get doesn't see — so Imaging /
        // Encounter never showed them (QA 2026-08-21). Pull the mirror list and
        // append only the local:true entries (its Athena rows would double the
        // ones procedures/get already returned).
        let localDocs = [];
        try {
          const cat = subview === 1 ? "imagingresult" : "encounterdocument";
          const wrapKey = subview === 1 ? "imagingresults" : "encounterdocuments";
          const mres = await careiqapi.get(
            `/v1/fhir-mirror/${practiseid}/patients/${patientId}/documents/${cat}`,
          );
          localDocs = (mres?.data?.[wrapKey] || mres?.[wrapKey] || [])
            .filter((d) => d?.local)
            .map((item) => ({
              ...item,
              normalizedDate: dayjs(item.createddate),
              createddate: dayjs(item.createddate),
              source: subview === 1 ? "imaging" : "encounter",
            }));
        } catch (e) {
          console.error("local docs fetch failed", e);
        }

        setProcedureData([...normalizedApiDocs, ...localDocs, ...emrDocs]);
      } catch (error) {
        console.error("Error fetching procedure data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (subview === 0) {
      // All
      fetchAllData();
    } else if (subview === 3) {
      // Clinical
      fetchClinicalDocuments();
    } else if (subview === 4) {
      fetchFormsData();
    } else {
      // Imaging (1) or Encounter (2)
      fetchProcedureData();
    }
  }, [searchdate, subview]);

  // Extract unique years
  const uniqueYears = Array.from(
    new Set(procedureData.map((item) => dayjs(item.createddate).year())),
  ).sort((a, b) => b - a);
  const tabs = [
    { label: "All", width: 64 },
    ...uniqueYears.map((year) => ({
      label: year.toString(),
      width: 64,
    })),
  ];

  if (uniqueYears.length > 2) {
    tabs.push({ label: "Archive", width: 64 });
  }

  useEffect(() => {
    if (selectedTab >= tabs.length) setSelectedTab(0);
  }, [tabs.length, selectedTab]);

  const dropdownOptions = tabs.map((t, i) => ({ label: t.label, value: i }));
  const sortByLatest = (data = []) =>
    [...data].sort(
      (a, b) => dayjs(b.createddate).valueOf() - dayjs(a.createddate).valueOf(),
    );

  const filteredData = () => {
    const selectedLabel = tabs[selectedTab]?.label;

    // ✅ ALL
    if (selectedLabel === "All") {
      return sortByLatest(procedureData);
    }

    // ✅ ARCHIVE
    if (selectedLabel === "Archive") {
      const maxYear = Math.max(...uniqueYears);
      return sortByLatest(
        procedureData.filter(
          (item) => dayjs(item.createddate).year() < maxYear - 1,
        ),
      );
    }

    // ✅ SPECIFIC YEAR
    const year = parseInt(selectedLabel);
    return sortByLatest(
      procedureData.filter((item) => dayjs(item.createddate).year() === year),
    );
  };

  // Group data by month/day
  const groupedProcedures = () => {
    const grouped = filteredData().reduce((acc, item) => {
      const month = dayjs(item.createddate).format("MMMM YYYY");
      const day = dayjs(item.createddate).format("D");

      if (!acc[month]) acc[month] = {};
      if (!acc[month][day]) acc[month][day] = [];
      acc[month][day].push(item);
      return acc;
    }, {});
    return Object.entries(grouped).map(([month, days]) => ({ month, days }));
  };

  const handleViewToggle = (value) => setView(value);

  // Reset pagination when filters/subview change
  useEffect(() => {
    setCurrentPage(1);
  }, [subview, selectedTab, view]);
  const handleHtml = async (row) => {
    try {
      setIsLoading(true);
      const response = await api.get(
        `/v1/${practiseid}/pcard/${patientId}/formHistory/${row.id}`,
      );
      if (response.status == 200) {
        setSelectedData(response.data || {});
        setHtmlModal(true);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className="flex flex-col justify-center px-3">
      <DocumentHeader
        onUpload={() => { setUploadMsg(""); setUploadOpen(true); }}
        data={dataId}
        viewStatus={view}
        handleView={handleViewToggle}
        searchdate={searchDate}
        setSearchDate={setSearchDate}
        setsubview={setSubView}
        subview={subview}
        options={dropdownOptions}
        value={selectedTab}
        onChange={setSelectedTab}
      />
      {/* The secondary "Upload Document" button was removed 2026-09-04 —
          the header "Upload" now opens this same modal (one path only). */}
      {uploadOpen && (
        <div className="fixed inset-0 z-[9600] flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-lg">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-base font-semibold">Upload Document</div>
              <button onClick={() => setUploadOpen(false)}>✕</button>
            </div>
            <label className="text-xs font-semibold">Type</label>
            <select value={uploadType} onChange={(e) => setUploadType(e.target.value)}
              className="mb-3 mt-1 w-full rounded-md border px-3 py-2 text-sm">
              <option value="clinicaldocument">Clinical document</option>
              <option value="imagingresult">Imaging result</option>
              <option value="encounterdocument">Encounter document</option>
            </select>
            <label className="text-xs font-semibold">Description</label>
            <input value={uploadDesc} onChange={(e) => setUploadDesc(e.target.value)}
              placeholder="e.g. Outside records — cardiology consult"
              className="mb-3 mt-1 w-full rounded-md border px-3 py-2 text-sm" />
            <label className="text-xs font-semibold">File (PDF/image)</label>
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              className="mb-3 mt-1 w-full text-sm" />
            {uploadMsg && <div className="mb-2 text-xs text-[#1D2951]">{uploadMsg}</div>}
            <div className="flex justify-end gap-2">
              <button onClick={() => setUploadOpen(false)}
                className="rounded-md border px-4 py-2 text-sm">Close</button>
              <button disabled={uploadBusy} onClick={handleLocalUpload}
                className={`rounded-md px-4 py-2 text-sm text-white ${uploadBusy ? "bg-gray-300" : "bg-[#1D2951]"}`}>
                {uploadBusy ? "Uploading…" : "Upload"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center w-[100%] mt-40">
          <CircularProgress />
        </div>
      ) : view ? (
        <>
          {(() => {
            const allData = filteredData();
            const totalItems = allData.length;
            const totalPages = Math.max(
              1,
              Math.ceil(totalItems / itemsPerPage),
            );
            const safeCurrentPage = Math.min(currentPage, totalPages);
            const startIndex = (safeCurrentPage - 1) * itemsPerPage;
            const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
            const pageItems = allData.slice(startIndex, endIndex);

            return (
              <>
                <div className="flex flex-col gap-4 overflow-auto max-h-screen p-4 h-[450px]">
                  {pageItems.length > 0 ? (
                    pageItems.map((item, index) =>
                      subview === 0 ? (
                        <DocumentAllCards
                          key={index}
                          procedure={item}
                          handleHtml={handleHtml}
                          subview={subview}
                          patid={dataId?.patientid}
                          docLabels={docLabels}
                          onLocalDeleted={handleLocalDeleted}
                        />
                      ) : (
                        <DocumentCardsList
                          key={index}
                          procedure={item}
                          handleHtml={handleHtml}
                          subview={subview}
                          patid={dataId?.patientid}
                          docLabels={docLabels}
                          onLocalDeleted={handleLocalDeleted}
                        />
                      ),
                    )
                  ) : (
                    <div className="text-center text-grey-200">
                      No Data Found
                    </div>
                  )}
                  {/* Pagination Footer */}
                  <div className="flex w-full items-center justify-between px-4 py-2">
                    <div className="text-[12px] text-[#6B7280] font-inter">
                      {totalItems > 0
                        ? `Showing ${startIndex + 1}- ${endIndex} of ${totalItems} entries`
                        : "Showing 0 of 0 entries"}
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-[#000000] font-inter text-[12px]">
                          per page
                        </span>
                        <PaginationDropdownPill
                          options={[
                            { label: "5", value: 5 },
                            { label: "10", value: 10 },
                            { label: "25", value: 25 },
                          ]}
                          value={itemsPerPage}
                          onChange={(val) => {
                            setItemsPerPage(Number(val));
                            setCurrentPage(1);
                          }}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          className={`px-3 py-1 text-[13px] font-inter rounded-md border ${
                            safeCurrentPage === 1
                              ? "text-gray-400 border-[#E5E7EB] cursor-not-allowed"
                              : "text-[#000000]  border-gray-300 hover:bg-[#FFF]"
                          }`}
                          onClick={() =>
                            safeCurrentPage > 1 &&
                            setCurrentPage(safeCurrentPage - 1)
                          }
                          disabled={safeCurrentPage === 1}
                        >
                          Previous
                        </button>
                        <div className="px-[10px] py-1 rounded-lg bg-[#1D2951] text-white">
                          {safeCurrentPage}
                        </div>
                        <button
                          className={`px-3 py-1 text-[13px] font-inter rounded-md border ${
                            safeCurrentPage === totalPages
                              ? "text-gray-400 border-gray-200 cursor-not-allowed"
                              : "text-[#000000]   border-gray-300 hover:bg-[#FFF]"
                          }`}
                          onClick={() =>
                            safeCurrentPage < totalPages &&
                            setCurrentPage(safeCurrentPage + 1)
                          }
                          disabled={safeCurrentPage === totalPages}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-2 h-[450px] overflow-y-auto">
          {groupedProcedures().length > 0 ? (
            groupedProcedures().map(({ month, days }, monthIndex) => (
              <div
                key={monthIndex}
                className="w-[100%] h-[312px] mt-3 flex flex-col items-start justify-start p-2 bg-white rounded-[15px] shadow-md border-t-2 border-[#1d2951]"
              >
                <div className="flex items-center mb-2 mx-1.5">
                  <span className="ml-2 font-bold text-[#1d2951] text-base">
                    {month}
                  </span>
                </div>

                <div
                  className="w-[100%] overflow-y-auto h-[200px] pt-6 flex flex-col justify-start"
                  style={{ scrollbarWidth: "none" }}
                >
                  <ul className="w-[100%] space-y-2">
                    {Object.entries(days)
                      .flatMap(([day, items]) =>
                        items.map((item, idx) => ({
                          item,
                          idx,
                          day,
                          fullDate: dayjs(item.createddate).format(
                            "MMMM D, YYYY",
                          ),
                        })),
                      )
                      .sort(
                        (a, b) =>
                          new Date(b.item.createddate) -
                          new Date(a.item.createddate),
                      )
                      .map(({ item, idx, day, fullDate }) => (
                        <div
                          key={`${day}-${idx}`}
                          className="flex flex-col justify-between items-center px-2"
                        >
                          <div className="flex w-full justify-between">
                            <div className="font-normal text-[#1D2951] text-[13px]">
                              {fullDate}
                            </div>
                            <div
                              onClick={(e) => {
                                e.preventDefault();
                                if (item?.imgbasefile) {
                                  setSelectedProcedure(item);
                                  setOpenBase64Viewer(true);
                                  return;
                                } else if (
                                  item.documentclass === "Onboarding"
                                ) {
                                  setSelectedView(item);
                                  setIsModal(true);
                                  return;
                                } else if (item.documentclass === "Form") {
                                  handleHtml(item);
                                  return;
                                }
                                setSelectedProcedure(item);
                                setIsProcedureDocumentOpen(true);
                              }}
                              className="flex gap-2 font-light text-[#1d2951] text-[13px] cursor-pointer"
                            >
                              <RemoveRedEyeIcon
                                style={{ width: "30px", height: "20px" }}
                              />
                              View Document
                            </div>
                          </div>
                          <div className="w-full h-[1px] bg-gray-300 my-1" />
                        </div>
                      ))}
                  </ul>
                </div>
              </div>
            ))
          ) : (
            <div className="text-grey-200 w-[100%] items-center">
              No Data Found
            </div>
          )}
        </div>
      )}
      <Base64ViewerModal
        open={openBase64Viewer}
        onClose={() => setOpenBase64Viewer(false)}
        base64={selectedProcedure?.imgbasefile}
      />
      <ProcedureDocument
        subview={subview}
        isProcedureDocumentOpen={isProcedureDocumentOpen}
        setIsProcedureDocumentOpen={setIsProcedureDocumentOpen}
        procedure={{ ...selectedProcedure, patientid: selectedProcedure?.patientid ?? dataId?.patientid }}
      />

      <OnBoardView
        ismodal={ismodal}
        setIsModal={setIsModal}
        data={selectedview}
      />
      {selectedData?.form_name === "Home Sleep Questionnaire" ? (
        <ViewDoc
          data={selectedData}
          ismodalopen={htmlModal}
          setHtmlModal={setHtmlModal}
        />
      ) : (
        <></>
      )}
      {selectedData?.form_name ===
      "The Patient Health Questionnaire (PHQ-9)" ? (
        <ViewPhq9
          data={selectedData}
          ismodalopen={htmlModal}
          setHtmlModal={setHtmlModal}
        />
      ) : (
        <></>
      )}
      {selectedData?.form_name === "Mood Disorder Questionnaire (MDQ)" ? (
        <ViewMdq
          data={selectedData}
          ismodalopen={htmlModal}
          setHtmlModal={setHtmlModal}
        />
      ) : (
        <></>
      )}
      {selectedData?.form_name ===
      "Adult ADHD Self Report Scale (ASRS-vI.I) Symptom Checklist" ? (
        <ViewAsrs
          data={selectedData}
          ismodalopen={htmlModal}
          setHtmlModal={setHtmlModal}
        />
      ) : (
        <></>
      )}
      {selectedData?.form_name === "Mini-Mental State Examination (MMSE)" ? (
        <ViewMmse
          data={selectedData}
          ismodalopen={htmlModal}
          setHtmlModal={setHtmlModal}
        />
      ) : (
        <></>
      )}
      {selectedData?.form_name === "STOP-BANG Sleep Apnea Questionnaire" ? (
        <ViewStopBang
          data={selectedData}
          ismodalopen={htmlModal}
          setHtmlModal={setHtmlModal}
        />
      ) : (
        <></>
      )}
    </div>
  );
};

export default Document;
